import {
  InspectionSubType,
  InspectionType,
  PaidFor,
  INSPECTION_TYPE_VALUES,
  INSPECTION_SUBTYPE_VALUES,
  BikeTypeV2,
  LeasingOptionType,
} from "@jobrad-gmbh/bike-leasing-calculator";
import { DefaultNamespace, TFunction } from "i18next";
import { LEASING_DURATION_YEARS } from "../../../constants/businessConstants.ts";

export const AllServiceValues = [...INSPECTION_TYPE_VALUES, ...INSPECTION_SUBTYPE_VALUES] as const;
export type AllServiceValuesType = (typeof AllServiceValues)[number];

export function getInspectionType(
  servicesType: InspectionType | boolean,
  servicesSubType: InspectionSubType | boolean,
): AllServiceValuesType | null {
  if (servicesType === "ivo" && servicesSubType === false) {
    return "ivo";
  }
  if (servicesType === "full" && servicesSubType === false) {
    return "full";
  }
  if (servicesType === "ivo" && servicesSubType === "basis") {
    return "basis";
  }
  if (servicesType === "full" && servicesSubType === "komfort") {
    return "komfort";
  }
  if (servicesType === "full" && servicesSubType === "unlimited") {
    return "unlimited";
  }

  return null;
}

export function getV1ServiceOptionByInspectionType(
  bikeServiceOptions: LeasingOptionType[],
  inspectionType: InspectionType,
): LeasingOptionType | undefined {
  return bikeServiceOptions.find((service) => service.service_type === inspectionType);
}

export function getV2IncludedInspectionPriceByBikeType(bikeServiceOptions: LeasingOptionType[], bikeType: BikeTypeV2) {
  // inspection_price is the same in each option per bike type.
  // 'kein'-ServiceOption for non-mandatory Service configurations does not contain the prices - we need to find the first option with prices
  const serviceContainingPrices = bikeServiceOptions.find((service) => service.default_bike.inspection_price !== 0);

  if (!serviceContainingPrices) {
    return 0;
  }

  return serviceContainingPrices[bikeType].inspection_price;
}

export function doesServiceCoverWearParts(companyPays: PaidFor | undefined) {
  return companyPays?.inspection?.servicesType === "full";
}

export function getLabelForInspection(t: TFunction<DefaultNamespace, undefined>, paidFor: PaidFor | undefined) {
  if (!paidFor?.inspection) {
    return "";
  }
  const inspectionType = getInspectionType(paidFor.inspection.servicesType, paidFor.inspection.servicesSubType);
  return getLabelForInspectionType(t, inspectionType);
}

export const companyPaysNothing = (companyPays?: PaidFor) => {
  const activeService = resolveActiveService(companyPays);
  const hasActiveService = !!activeService;
  return !(hasActiveService || companyPays?.insurance);
};

export const companyPaysEverything = (companyPays?: PaidFor) => {
  const activeService = resolveActiveService(companyPays);
  const hasActiveService = !!activeService;
  return hasActiveService && companyPays?.insurance;
};

export function getLabelForInspectionType(
  t: TFunction<DefaultNamespace, undefined>,
  inspectionType: AllServiceValuesType | null,
) {
  if (inspectionType === "ivo") {
    return t("inspection.ivo");
  }
  if (inspectionType === "full") {
    return t("inspection.full");
  }
  if (inspectionType === "basis") {
    return t("inspection.basis");
  }
  if (inspectionType === "komfort") {
    return t("inspection.komfort");
  }
  if (inspectionType === "unlimited") {
    return t("inspection.unlimited");
  }
  return null;
}

export function resolveActiveServices(employeePays?: PaidFor, companyPays?: PaidFor): AllServiceValuesType | null {
  const activeServiceCompany = resolveActiveService(companyPays);
  const activeServiceEmployee = resolveActiveService(employeePays);
  if (activeServiceCompany !== null) {
    return activeServiceCompany;
  }
  if (activeServiceEmployee !== null) {
    return activeServiceEmployee;
  }
  return null;
}

export function resolveActiveService(paysFor?: PaidFor): AllServiceValuesType | null {
  let activeService;
  if (paysFor?.inspection) {
    activeService = getInspectionType(paysFor.inspection.servicesType, paysFor.inspection.servicesSubType);
  }
  if (activeService) {
    return activeService;
  }

  return null;
}

export function convertToRateRangeArray(obj: unknown) {
  if (Array.isArray(obj)) {
    return obj;
  } else if (typeof obj === "string") {
    if (obj === "") {
      return [];
    }
    // the backend sends us a string containing an array with an object using single quotes as property quotes
    // like so "[{'min': 749, 'max': 1000, 'val': 1.73}]"
    const correctedJsonString = obj.replaceAll("'", '"');
    return JSON.parse(correctedJsonString);
  } else {
    return [];
  }
}

export const isUnlimited = (service: AllServiceValuesType | null): boolean => service === "unlimited";

export function calculateWearPartsBudgetForServiceOption(serviceOption: LeasingOptionType | undefined) {
  if (!serviceOption) {
    return;
  }

  return serviceOption.default_bike.budget - LEASING_DURATION_YEARS * serviceOption.default_bike.inspection_price;
}
